import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type SyntheticEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  title: string;
  eyebrow?: string;
  size?: 'default' | 'wide';
  /**
   * Protege por defecto los valores modificados por la persona usuaria.
   * Usa `false` únicamente en diálogos con campos exploratorios que no se guardan.
   */
  protectUnsavedChanges?: boolean;
  /** Impide Escape, backdrop, cierre y descarte mientras una operación está en curso. */
  preventClose?: boolean;
  onClose: () => void;
  children: ReactNode;
}

/** Modal responsivo: hoja inferior en móvil, diálogo centrado en escritorio. */
export function Modal({
  open,
  title,
  eyebrow,
  size = 'default',
  protectUnsavedChanges = true,
  preventClose = false,
  onClose,
  children,
}: ModalProps) {
  const titleId = useId();
  const discardDescriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const continueEditingRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const focusBeforeDiscardRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const preventCloseRef = useRef(preventClose);
  const dismissRequestRef = useRef<() => void>(() => undefined);
  const dirtyRef = useRef(false);
  const confirmingDiscardRef = useRef(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [keyboardViewport, setKeyboardViewport] = useState<{
    height: number;
    bottom: number;
  } | null>(null);

  const markUnsavedChanges = () => {
    if (!protectUnsavedChanges || confirmingDiscardRef.current) return;
    dirtyRef.current = true;
    setHasUnsavedChanges(true);
  };

  const continueEditing = () => {
    confirmingDiscardRef.current = false;
    setConfirmingDiscard(false);
    window.requestAnimationFrame(() => {
      const target = focusBeforeDiscardRef.current;
      if (target?.isConnected) target.focus();
      else dialogRef.current?.focus();
    });
  };

  const requestClose = () => {
    if (preventCloseRef.current) return;
    if (confirmingDiscardRef.current) {
      continueEditing();
      return;
    }
    if (protectUnsavedChanges && dirtyRef.current) {
      focusBeforeDiscardRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      confirmingDiscardRef.current = true;
      setConfirmingDiscard(true);
      return;
    }
    onCloseRef.current();
  };

  dismissRequestRef.current = requestClose;

  const discardChanges = () => {
    if (preventCloseRef.current) return;
    dirtyRef.current = false;
    confirmingDiscardRef.current = false;
    setHasUnsavedChanges(false);
    setConfirmingDiscard(false);
    onCloseRef.current();
  };

  const markFromFieldEvent = (event: SyntheticEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-ignore-unsaved-changes="true"]')) return;
    markUnsavedChanges();
  };

  const markFromValueControl = (event: ReactMouseEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('[data-form-value-change="true"]')) return;
    markUnsavedChanges();
  };

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  preventCloseRef.current = preventClose;

  useEffect(() => {
    dirtyRef.current = false;
    confirmingDiscardRef.current = false;
    setHasUnsavedChanges(false);
    setConfirmingDiscard(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismissRequestRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(
        (element) =>
          !element.hasAttribute('hidden') && !element.closest('[hidden], [aria-hidden="true"]'),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    const focusDialog = window.requestAnimationFrame(() => {
      // Favorece el campo marcado por el formulario y, si no existe, mantiene
      // el foco dentro del diálogo. También cubre la transición entre dos hojas.
      const preferredTarget = dialogRef.current?.querySelector<HTMLElement>(
        '[data-modal-autofocus="true"], [autofocus]',
      );
      (preferredTarget ?? dialogRef.current)?.focus();
    });
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
      window.cancelAnimationFrame(focusDialog);
      previouslyFocusedRef.current?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open || !confirmingDiscard) return undefined;
    const focusConfirmation = window.requestAnimationFrame(() => {
      continueEditingRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(focusConfirmation);
  }, [confirmingDiscard, open]);

  useEffect(() => {
    if (!open) return undefined;
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const update = () => {
      const bottom = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      setKeyboardViewport(bottom > 80 ? { height: viewport.height, bottom } : null);
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
      setKeyboardViewport(null);
    };
  }, [open]);

  if (!open) return null;
  // The route content area is scrollable on desktop. Rendering at document.body
  // prevents that container from clipping the viewport-wide backdrop.
  return createPortal(
    <div
      data-testid="modal-backdrop"
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/40 p-0 backdrop-blur-[2px] sm:items-center sm:p-5"
      style={keyboardViewport ? { paddingBottom: keyboardViewport.bottom } : undefined}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) dismissRequestRef.current();
      }}
    >
      <div
        ref={dialogRef}
        className={`modal-enter flex max-h-[calc(100dvh-12px)] w-full max-w-[460px] flex-col overflow-hidden rounded-t-[26px] border border-white/70 bg-white shadow-[0_24px_64px_-18px_rgba(15,23,42,.45)] sm:max-h-[calc(100dvh-40px)] sm:rounded-[22px] ${size === 'wide' ? 'lg:max-w-4xl' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={confirmingDiscard ? discardDescriptionId : undefined}
        aria-busy={preventClose || undefined}
        data-has-unsaved-changes={hasUnsavedChanges ? 'true' : undefined}
        tabIndex={-1}
        style={
          keyboardViewport
            ? ({ maxHeight: Math.max(240, keyboardViewport.height - 12) } as CSSProperties)
            : undefined
        }
        onInputCapture={markFromFieldEvent}
        onChangeCapture={markFromFieldEvent}
        onClickCapture={markFromValueControl}
      >
        <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-slate-200 sm:hidden" />
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            {!confirmingDiscard && eyebrow && (
              <p className="mb-0.5 text-xs font-bold uppercase tracking-[.08em] text-slate-600">
                {eyebrow}
              </p>
            )}
            <h2 id={titleId} className="text-[19px] font-bold tracking-tight text-slate-900">
              {confirmingDiscard ? '¿Descartar lo que escribiste?' : title}
            </h2>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={preventClose}
            className="tap-target flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-wait disabled:opacity-40"
            aria-label={
              preventClose
                ? 'Guardando; espera para cerrar'
                : confirmingDiscard
                  ? 'Volver al formulario'
                  : 'Cerrar'
            }
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <div className="min-h-0 overscroll-contain overflow-y-auto px-5 py-5 pb-[calc(20px+env(safe-area-inset-bottom))] sm:px-6 sm:pb-6">
          <div hidden={confirmingDiscard} aria-hidden={confirmingDiscard || undefined}>
            {children}
          </div>
          {confirmingDiscard && (
            <div className="space-y-5">
              <div className="flex gap-3">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-50 text-lg font-bold text-amber-700"
                  aria-hidden
                >
                  !
                </span>
                <p id={discardDescriptionId} className="text-sm leading-6 text-slate-600">
                  Los cambios que todavía no guardaste se perderán.
                </p>
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  ref={continueEditingRef}
                  type="button"
                  onClick={continueEditing}
                  className="button button-secondary w-full sm:w-auto"
                >
                  Seguir editando
                </button>
                <button
                  type="button"
                  onClick={discardChanges}
                  className="button button-danger w-full sm:w-auto"
                >
                  Descartar cambios
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
